import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import type {
  IRegistrationPerson,
  IRegistrationPersonLookup,
  IRegistrationPersonPatch,
} from '../entities/registration-person.model';
import { PersonService } from './person.service';

/**
 * The requests of the counter for a link registration sent by someone who is
 * ALREADY a person of the gym: who the CPF belongs to, that person with phones
 * and addresses, the update of that person, and whether the link's e-mail is
 * free to become a login.
 *
 * Plain requests of this lib, for the reason `RegistrationHealthService` gives:
 * the profile's data lib already imports this one, so its `ProfileService`
 * cannot be reached from here. Its `ProfileStore.updatePerson` could not serve
 * either way: it is a root store, and it merges the last profile opened onto
 * whatever id it is handed.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationPersonService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;
  private readonly personService = inject(PersonService);

  /**
   * Who of the gym a CPF belongs to, active or not and whatever the
   * registration type: a customer, a visitor or a contact, and a staff member,
   * a supplier or a helpdesk record just the same. The lookup looks at the CPF
   * and at nothing else.
   *
   * `IsFiscalIdentityAvailable` cannot answer this. It sees active customers
   * only and gives no id, so the CPF of a visitor or of an inactive customer
   * passed for free and a second person was created with it.
   *
   * The gym comes from the bearer. `PersonTypeCode: 'F'` is a natural person,
   * looked up by CPF. With the same CPF on two people the service answers
   * either of them.
   *
   * A 403 here opens no supervisor prompt: nobody at the counter asked for this
   * request, and the answer that follows a failure is a closed confirmation.
   */
  public findByFiscalIdentity(fiscalIdentity: string): Observable<IRegistrationPersonLookup> {
    return this.http.post<IRegistrationPersonLookup>(
      `${this.apiUrl.personService}/Person/LookupByDocumentFromExternalCrm`,
      { Payload: { PersonTypeCode: 'F', Document: (fiscalIdentity ?? '').replaceAll(/\D/g, '') } },
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /**
   * The person with phones and addresses: what the counter compares with the
   * link's data, and what every update body is built from.
   *
   * The same request the supplier screen reads a supplier with, reused as it
   * is. Its declared `IPerson` leaves out the two lists the request asks for,
   * hence the type given here.
   */
  public getPersonWithContacts(personId: string): Observable<IRegistrationPerson> {
    return this.personService
      .getSupplierById(personId)
      .pipe(map((person: unknown) => person as IRegistrationPerson));
  }

  /**
   * Writes the link's data on the person. The body says what: see
   * `IRegistrationPersonPatch` for why it is built by omission.
   *
   * No supervisor prompt on a 403. Whether this staff member may update the
   * person is asked silently before the choices are offered, and a refusal here
   * is a failed step of the chain, to be tried again.
   */
  public updatePerson(body: IRegistrationPersonPatch): Observable<IRegistrationPerson> {
    return this.http.patch<IRegistrationPerson>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      body,
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /**
   * Whether a login of the gym already uses the e-mail: `true` is "taken". The
   * same question the profile's form asks before it saves an e-mail.
   *
   * The person's e-mail is the login. For a person the user service already
   * knows, it mirrors the e-mail from the person, and an e-mail that is
   * somebody else's login is not refused there: the person is moved onto the
   * credential that already has it, and the two people share one login and one
   * password. Hence the check.
   */
  public isEmailUnavailable(email: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl.userService}/verification/IsEmailUnavailable`, {
      params: { email },
      context: withoutForbiddenElevation(),
      withCredentials: true,
    });
  }
}
